import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { APIProvider, Map, AdvancedMarker, useMapsLibrary, useMap } from '@vis.gl/react-google-maps';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faHourglassHalf, faLocationDot, faCamera, faTrash, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { coralSelectStyles, coralSelectTheme } from './coralSelect';
import TaskListEditor from './TaskListEditor';
import './clientRequests.css';
import { AREA_OPTIONS } from '../../../constants/areas';
import { formatAreaOption } from '../../../constants/areaOption';

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';
const MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';
const START_POS = { lat: 25.2048, lng: 55.2708 }; // Dubai
const isValidCoord = (n) => Number.isFinite(n) && Math.abs(n) > 0.000001;

/* --- Map helpers (mirrors RequestPropertyModal) --- */
function PlacesSearch({ onPick }) {
    const placesLib = useMapsLibrary('places');
    const hostRef = useRef(null);
    const elementRef = useRef(null);

    useEffect(() => {
        if (!placesLib || !hostRef.current || elementRef.current) return;
        const host = hostRef.current;
        const el = new window.google.maps.places.PlaceAutocompleteElement();
        try { el.includedRegionCodes = ['ae']; } catch { /* noop */ }
        el.placeholder = 'Search your building or address';
        el.style.width = '100%';
        el.style.height = '42px';
        el.style.border = '1px solid #e2e2e2';
        el.style.borderRadius = '12px';
        el.style.background = '#fff';

        const onSelect = async (evt) => {
            const pp = evt?.placePrediction;
            if (!pp) return;
            const place = pp.toPlace();
            await place.fetchFields({ fields: ['id', 'location', 'displayName', 'googleMapsLinks'] });
            const loc = place.location;
            if (!loc) return;
            onPick({
                lat: loc.lat(),
                lng: loc.lng(),
                placeId: place.id || null,
                placeDesc: place.displayName || '',
                mapsUri: place.googleMapsLinks?.placeURI || null,
            });
        };

        el.addEventListener('gmp-select', onSelect);
        host.appendChild(el);
        elementRef.current = el;
        return () => {
            try {
                el.removeEventListener('gmp-select', onSelect);
                if (host.contains(el)) host.removeChild(el);
            } catch { /* noop */ }
            elementRef.current = null;
        };
    }, [placesLib, onPick]);

    return <div ref={hostRef} />;
}

function MapClick({ onPick }) {
    const map = useMap();
    useEffect(() => {
        if (!map) return;
        const listener = map.addListener('click', (e) => {
            const lat = e.latLng?.lat?.();
            const lng = e.latLng?.lng?.();
            if (typeof lat === 'number' && typeof lng === 'number') onPick({ lat, lng });
        });
        return () => listener?.remove?.();
    }, [map, onPick]);
    return null;
}

function PanTo({ position, zoom = 16 }) {
    const map = useMap();
    useEffect(() => {
        if (!map || !position) return;
        map.panTo(position);
        map.setZoom(zoom);
    }, [map, position, zoom]);
    return null;
}

const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BEDROOM_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8]
    .map((n) => ({ value: n, label: n === 0 ? 'Studio' : String(n) }));
// Half steps = bathroom + guest toilet (e.g. 2.5).
const BATHROOM_OPTIONS = [0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10]
    .map((n) => ({ value: n, label: String(n) }));
const BED_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({ value: n, label: String(n) }));

// Mirrors the create form's list

function Toggle({ checked, onChange, label, hint, className = '' }) {
    return (
        <button
            type="button"
            className={`creq-toggle ${checked ? 'is-on' : ''} ${className}`}
            onClick={() => onChange(!checked)}
        >
            <span className="creq-toggle-track"><span className="creq-toggle-thumb" /></span>
            <span className="creq-toggle-text">
                {label}
                {hint && <span className="creq-toggle-hint">{hint}</span>}
            </span>
        </button>
    );
}

// Client "Request Changes" modal for an existing property — parity with the
// create form (everything except the map pin). Pricing-affecting fields are
// flagged to the admin in the review diff. Submits SPARSE changes.
export default function RequestPropertyEditModal({ property, onClose, onSubmitted }) {
    const original = useMemo(() => ({
        address_line_1: property.building_name || '',
        unit_number: property.unit_number || '',
        area: property.area || '',
        bedrooms: property.bedrooms ?? 0,
        bathrooms: property.bathrooms ?? 0,
        single_beds: property.single_beds ?? 0,
        double_beds: property.double_beds ?? 0,
        super_king_beds: property.super_king_beds ?? 0,
        required_cleaners: property.required_cleaners ?? 1,
        is_beach_towel: !!property.is_beach_towel,
        is_dolce_gusto: !!property.is_dolce_gusto,
        is_nespresso: !!property.is_nespresso,
        property_access: property.property_access || '',
        wifi_name: property.wifi_name || '',
        wifi_password: property.wifi_password || '',
        notes: property.permanent_notes || '',
    }), [property]);

    const [form, setForm] = useState({ ...original });
    // Billed-To (multi-billing only, 2026-08-10): preloaded with the
    // property's current entity; the section renders only when the account
    // actually has entities.
    const [billingEntities, setBillingEntities] = useState([]);
    const [billingProfileId, setBillingProfileId] = useState(property.billing_profile_id ?? null);
    useEffect(() => {
        let stale = false;
        axios.get('/api/client-requests/requests/billing-entities')
            .then((res) => { if (!stale) setBillingEntities(res.data?.entities || []); })
            .catch(() => { if (!stale) setBillingEntities([]); });
        return () => { stale = true; };
    }, []);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    // Map location — tracked separately (float tolerance, not string compare)
    const origLoc = useMemo(() => ({
        latitude: Number(property.latitude),
        longitude: Number(property.longitude),
        google_maps_link: property.google_maps_link || '',
        google_place_id: property.google_place_id || '',
        google_place_desc: property.google_place_desc || '',
    }), [property]);
    const [loc, setLoc] = useState({ ...origLoc });
    // Admin-panel behaviour: the map starts LOCKED when a pin exists — an
    // overlay intercepts clicks until the client deliberately taps to edit.
    const [isEditingLocation, setIsEditingLocation] = useState(
        () => !(isValidCoord(Number(property.latitude)) && isValidCoord(Number(property.longitude)))
    );
    const locationChanged = (isValidCoord(loc.latitude) && isValidCoord(loc.longitude))
        && (!isValidCoord(origLoc.latitude)
            || Math.abs(loc.latitude - origLoc.latitude) > 0.000001
            || Math.abs(loc.longitude - origLoc.longitude) > 0.000001);
    const position = useMemo(() => (
        isValidCoord(loc.latitude) && isValidCoord(loc.longitude)
            ? { lat: loc.latitude, lng: loc.longitude } : null
    ), [loc.latitude, loc.longitude]);

    const handlePlacePick = useCallback(({ lat, lng, placeId, placeDesc, mapsUri }) => {
        setLoc((prev) => ({
            ...prev,
            latitude: lat,
            longitude: lng,
            google_place_id: placeId || prev.google_place_id,
            google_place_desc: placeDesc || prev.google_place_desc,
            google_maps_link: mapsUri || prev.google_maps_link,
        }));
    }, []);
    // Map tap = raw coordinates: the old place identity no longer applies, so
    // clear it and rebuild the maps link from the pin (mirrors the admin panel
    // and the create-request modal) — navigation always points at the real pin.
    const handleMapPick = useCallback(({ lat, lng }) => {
        setLoc((prev) => ({
            ...prev,
            latitude: lat,
            longitude: lng,
            google_place_id: '',
            google_place_desc: '',
            google_maps_link: `https://maps.google.com/?q=${lat},${lng}`,
        }));
    }, []);

    // New setup photos (uploaded to S3 at submit, linked at approval)
    const [photos, setPhotos] = useState([]); // [{ file, previewUrl, description }]
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    // Setup photos already on the property — the client can mark them for
    // removal or change their note; both are applied at approval.
    const [existingPhotos, setExistingPhotos] = useState([]); // [{ id, file_url, description }]
    const [removedIds, setRemovedIds] = useState([]); // attachment ids marked for removal
    const [descEdits, setDescEdits] = useState({}); // { [attachmentId]: draft note }
    useEffect(() => {
        let stale = false;
        axios.get(`/api/client-requests/properties/${property.property_id}/setup-photos`)
            .then((res) => { if (!stale) setExistingPhotos(Array.isArray(res.data) ? res.data : []); })
            .catch(() => { if (!stale) setExistingPhotos([]); }); // 404 = no photos yet
        return () => { stale = true; };
    }, [property.property_id]);

    // Property TEXT tasks (non-photo) — editable list. Loaded from the property.
    const [propTasks, setPropTasks] = useState([]);
    const [propTasksOriginal, setPropTasksOriginal] = useState([]);
    useEffect(() => {
        let stale = false;
        axios.get(`/api/client-requests/properties/${property.property_id}/tasks`)
            .then((res) => {
                if (stale) return;
                const rows = (Array.isArray(res.data) ? res.data : [])
                    .map((t) => ({ id: t.id, instruction: t.instruction, is_report_required: !!t.is_report_required }));
                setPropTasks(rows);
                setPropTasksOriginal(rows);
            })
            .catch(() => { if (!stale) { setPropTasks([]); setPropTasksOriginal([]); } });
        return () => { stale = true; };
    }, [property.property_id]);

    const cleanTasks = (arr) => arr
        .map((t) => ({ id: t.id ?? null, instruction: (t.instruction || '').trim(), is_report_required: !!t.is_report_required }))
        .filter((t) => t.instruction);
    const propTasksChanged = JSON.stringify(cleanTasks(propTasks)) !== JSON.stringify(cleanTasks(propTasksOriginal));

    const toggleRemoveExisting = (id) => {
        setRemovedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };
    // Only notes that actually differ from what's stored count as changes; a
    // photo marked for removal never sends a note update (removal wins).
    const photoDescUpdates = existingPhotos
        .filter((ph) => !removedIds.includes(ph.id))
        .filter((ph) => descEdits[ph.id] !== undefined && descEdits[ph.id].trim() !== (ph.description || ''))
        .map((ph) => ({ id: ph.id, description: descEdits[ph.id].trim() }));

    // "Make permanent task" toggle per existing photo. taskEdits holds the
    // DRAFT state (true=task, false=no task); only photos whose draft differs
    // from the stored state (ph.task_id) become an actual change on submit.
    const [taskEdits, setTaskEdits] = useState({}); // { [attachmentId]: boolean }
    const toggleTaskExisting = (ph) => {
        const current = taskEdits[ph.id] ?? !!ph.task_id;
        setTaskEdits((prev) => ({ ...prev, [ph.id]: !current }));
    };
    const photoTaskToggles = existingPhotos
        .filter((ph) => !removedIds.includes(ph.id))
        .filter((ph) => taskEdits[ph.id] !== undefined && taskEdits[ph.id] !== !!ph.task_id)
        .map((ph) => ({ id: ph.id, make_task: taskEdits[ph.id] }));

    // Photo-backed tasks currently "on" (respecting draft toggles) — shown
    // read-only inside the Tasks editor so the client sees they exist.
    const photoTasksForEditor = existingPhotos
        .filter((ph) => !removedIds.includes(ph.id))
        .filter((ph) => (taskEdits[ph.id] ?? !!ph.task_id))
        .map((ph) => ({
            id: ph.id,
            instruction: (descEdits[ph.id] ?? ph.description) || 'Take this photo at each cleaning',
            photo_url: ph.file_url,
        }));

    const addPhotoFiles = (files) => {
        const images = files.filter((f) => f.type.startsWith('image/'));
        setPhotos((prev) => [
            ...prev,
            ...images.slice(0, 10 - prev.length).map((file) => ({
                file,
                previewUrl: URL.createObjectURL(file),
                description: '',
            })),
        ]);
    };
    const handlePickPhotos = (e) => {
        addPhotoFiles(Array.from(e.target.files || []));
        e.target.value = '';
    };
    const setPhotoDescription = (index, description) => {
        setPhotos((prev) => prev.map((ph, i) => (i === index ? { ...ph, description } : ph)));
    };
    const removePhoto = (index) => {
        setPhotos((prev) => {
            const removed = prev[index];
            if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
            return prev.filter((_, i) => i !== index);
        });
    };
    const uploadPhotos = async () => {
        const uploaded = [];
        for (const photo of photos) {
            const res = await axios.post('/api/client-requests/requests/properties/upload-photo-url', {
                fileName: photo.file.name,
                fileType: photo.file.type,
            });
            const { url, fileKey, fileUrl } = res.data;
            await fetch(url, {
                method: 'PUT',
                headers: { 'Content-Type': photo.file.type, 'x-amz-acl': 'public-read' },
                body: photo.file,
            });
            uploaded.push({ file_key: fileKey, file_url: fileUrl, description: photo.description.trim() });
        }
        return uploaded;
    };

    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));
    const norm = (v) => (v == null ? '' : String(v).trim());

    const changes = useMemo(() => {
        const c = {};
        for (const key of Object.keys(original)) {
            c[key] = norm(form[key]) !== norm(original[key]);
        }
        // The extra-cleaner option only exists for 2-bedroom apartments; if the
        // client moves bedrooms away from 2, the hidden control's stale value
        // must NOT ride along as a silent change.
        if (Number(form.bedrooms) !== 2) c.required_cleaners = false;
        return c;
    }, [form, original]);

    // Live price recalculation when a pricing-affecting field changes — the
    // SAME calculator that reprices the property at approval, so the client
    // sees exactly what their new All Inclusive price would be.
    const pricingTouched = ['bedrooms', 'single_beds', 'double_beds', 'super_king_beds', 'required_cleaners', 'is_beach_towel', 'is_dolce_gusto', 'is_nespresso']
        .some((f) => changes[f]);
    const [price, setPrice] = useState(null);
    const [priceLoading, setPriceLoading] = useState(false);
    useEffect(() => {
        if (!pricingTouched) { setPrice(null); setPriceLoading(false); return undefined; }
        setPriceLoading(true);
        let stale = false;
        const handle = setTimeout(async () => {
            try {
                const res = await axios.post('/api/client-requests/requests/properties/price-preview', {
                    // property_id lets the server preserve this property's
                    // negotiated price (not just the standard formula), so the
                    // previewed "new price" matches what approval will write.
                    property_id: property.property_id,
                    bedrooms: form.bedrooms,
                    single_beds: form.single_beds,
                    double_beds: form.double_beds,
                    super_king_beds: form.super_king_beds,
                    required_cleaners: Number(form.bedrooms) === 2 ? form.required_cleaners : original.required_cleaners,
                    is_linen_hire: property.is_linen_hire !== false,
                    is_beach_towel: form.is_beach_towel,
                    is_dolce_gusto: form.is_dolce_gusto,
                    is_nespresso: form.is_nespresso,
                });
                if (!stale) setPrice(res.data?.price || null);
            } catch (err) {
                if (!stale) setPrice(null);
            } finally {
                if (!stale) setPriceLoading(false);
            }
        }, 350);
        return () => { stale = true; clearTimeout(handle); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pricingTouched, form.bedrooms, form.single_beds, form.double_beds, form.super_king_beds, form.required_cleaners, form.is_beach_towel, form.is_dolce_gusto, form.is_nespresso]);
    const changedKeys = Object.keys(changes).filter((k) => changes[k]);
    const billingChanged = billingEntities.length > 0
        && (billingProfileId ?? null) !== (property.billing_profile_id ?? null);
    const hasChanges = changedKeys.length > 0 || locationChanged || photos.length > 0
        || removedIds.length > 0 || photoDescUpdates.length > 0 || photoTaskToggles.length > 0
        || propTasksChanged || billingChanged;
    // Bed minimum only applies when the client is changing beds (a legacy 0-bed
    // property can still edit wifi/access/notes) — mirrors the server rule.
    const bedsInvalid = (changes.single_beds || changes.double_beds || changes.super_king_beds)
        && (Number(form.single_beds) || 0) + (Number(form.double_beds) || 0) + (Number(form.super_king_beds) || 0) === 0;

    const submit = async () => {
        // Never submit mid-recalculation — the client must see the new price first.
        if (!hasChanges || bedsInvalid || submitting || (pricingTouched && priceLoading)) return;
        setSubmitting(true);
        setError('');
        try {
            const body = {};
            for (const key of changedKeys) body[key] = form[key];
            // The standard cleaner count follows the size (3+ BR = a team of 2).
            // changes.required_cleaners is suppressed for non-2BR sizes, so when
            // the size itself changed, carry the (already-standardised) count.
            if (changes.bedrooms) body.required_cleaners = Number(form.required_cleaners);
            if (locationChanged) {
                body.latitude = loc.latitude;
                body.longitude = loc.longitude;
                body.google_maps_link = loc.google_maps_link || undefined;
                body.google_place_id = loc.google_place_id || undefined;
                body.google_place_desc = loc.google_place_desc || undefined;
            }
            if (photos.length > 0) {
                body.setup_photos = await uploadPhotos();
            }
            if (removedIds.length > 0) body.setup_photo_removals = removedIds;
            if (photoDescUpdates.length > 0) body.setup_photo_updates = photoDescUpdates;
            if (photoTaskToggles.length > 0) body.setup_photo_task_toggles = photoTaskToggles;
            if (propTasksChanged) body.property_tasks = cleanTasks(propTasks);
            if (billingChanged) body.billing_profile_id = billingProfileId;
            await axios.post(`/api/client-requests/requests/properties/${property.property_id}/edit`, body);
            setDone(true);
            if (onSubmitted) onSubmitted();
        } catch (err) {
            console.error('Error submitting property change request:', err);
            setError(err.response?.data?.error || 'Failed to submit the change request.');
        } finally {
            setSubmitting(false);
        }
    };

    const tag = (key) => changes[key] && <span className="creq-changed-tag">changed</span>;

    return (
        <div className="creq-modal-overlay" onClick={onClose}>
            <div className="creq-modal creq-modal--wide" onClick={(e) => e.stopPropagation()}>
                {done ? (
                    <div className="creq-success">
                        <div className="creq-pending-badge">
                            <FontAwesomeIcon icon={faHourglassHalf} />
                        </div>
                        <span className="creq-pending-chip">Pending review</span>
                        <h3>Change requested</h3>
                        <p>
                            Your property stays as-is until our team approves the change —
                            we&apos;ll notify you the moment it&apos;s reviewed.
                        </p>
                        <button className="creq-btn creq-btn--primary" onClick={onClose}>Done</button>
                    </div>
                ) : (
                    <>
                        <div className="creq-modal-header">
                            <div>
                                <h3>Edit Property</h3>
                                <div className="creq-modal-sub">
                                    Unit {property.unit_number} · {property.building_name}
                                </div>
                            </div>
                            <button className="creq-modal-close" onClick={onClose} title="Close">
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="creq-modal-body">
                            {/* ── Building details ── */}
                            <div className="creq-section-label">Building details</div>
                            <div className="creq-grid">
                                <label className="creq-label">
                                    Building name {tag('address_line_1')}
                                    <input
                                        type="text"
                                        maxLength={120}
                                        value={form.address_line_1}
                                        onChange={(e) => set({ address_line_1: e.target.value })}
                                    />
                                </label>
                                <label className="creq-label">
                                    Unit number {tag('unit_number')}
                                    <input
                                        type="text"
                                        maxLength={30}
                                        value={form.unit_number}
                                        onChange={(e) => set({ unit_number: e.target.value })}
                                    />
                                </label>
                                <label className="creq-label creq-label--wide">
                                    Area {tag('area')}
                                    <Select
                                        options={AREA_OPTIONS}
                                        value={AREA_OPTIONS.find((o) => o.value === form.area) || (form.area ? { value: form.area, label: form.area } : null)}
                                        onChange={(opt) => set({ area: opt ? opt.value : '' })}
                                        styles={coralSelectStyles}
                                        theme={coralSelectTheme}
                                        menuPortalTarget={document.body}
                                        placeholder="Select area"
                                      formatOptionLabel={formatAreaOption}
          />
                                </label>
                                {/* Billed-To (multi-billing accounts only) — preloaded with the
                                    property's current entity; staff-reviewed like everything else. */}
                                {billingEntities.length > 0 && (
                                    <label className="creq-label">
                                        Billed to {billingChanged && <span className="creq-changed-tag">changed</span>}
                                        <Select
                                            options={billingEntities.map((e) => ({
                                                value: e.billing_profile_id, // null = default entity
                                                label: e.label + (e.is_default ? ' (default)' : ''),
                                            }))}
                                            value={(() => {
                                                const opts = billingEntities.map((e) => ({
                                                    value: e.billing_profile_id,
                                                    label: e.label + (e.is_default ? ' (default)' : ''),
                                                }));
                                                return opts.find((o) => (o.value ?? null) === (billingProfileId ?? null)) || null;
                                            })()}
                                            onChange={(opt) => setBillingProfileId(opt ? opt.value : null)}
                                            styles={coralSelectStyles}
                                            theme={coralSelectTheme}
                                            menuPortalTarget={document.body}
                                            placeholder="Select billing entity"
                                        />
                                    </label>
                                )}
                            </div>

                            {/* ── Map location ── */}
                            {GMAPS_KEY && (
                                <APIProvider apiKey={GMAPS_KEY} libraries={['places']}>
                                    <div className="creq-map-block">
                                        <div className="creq-section-label" style={{ marginTop: 0 }}>
                                            Map location {locationChanged && <span className="creq-changed-tag">changed</span>}
                                        </div>
                                        {!isEditingLocation && position ? (
                                            // Locked view: current pin + "Change" button (same as admin panel)
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 12,
                                                    padding: '10px 12px',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: 8,
                                                    background: '#fafafa',
                                                    marginBottom: 10,
                                                }}
                                            >
                                                <div style={{ minWidth: 0 }}>
                                                    <div style={{ fontWeight: 600, fontSize: 14 }}>Already pinned for this property</div>
                                                    <div
                                                        style={{
                                                            fontSize: 13,
                                                            color: '#4b5563',
                                                            marginTop: 2,
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            maxWidth: '100%',
                                                        }}
                                                        title={loc.google_place_desc || `${Number(loc.latitude).toFixed(6)}, ${Number(loc.longitude).toFixed(6)}`}
                                                    >
                                                        {loc.google_place_desc
                                                            || `${Number(loc.latitude).toFixed(6)}, ${Number(loc.longitude).toFixed(6)}`}
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setIsEditingLocation(true)}
                                                    style={{
                                                        height: 34,
                                                        padding: '0 12px',
                                                        borderRadius: 8,
                                                        border: '1px solid #e5e7eb',
                                                        background: '#fff',
                                                        cursor: 'pointer',
                                                        fontWeight: 600,
                                                        color: '#262626ff',
                                                        fontSize: 12,
                                                        flex: 'none',
                                                    }}
                                                    aria-label="Change pinned address"
                                                    title="Change pinned address"
                                                >
                                                    Change
                                                </button>
                                            </div>
                                        ) : (
                                            // Editing view: the actual Places search field
                                            <div className="creq-map-search">
                                                <FontAwesomeIcon icon={faLocationDot} className="creq-map-pin-icon" />
                                                <div className="creq-map-search-input">
                                                    <PlacesSearch onPick={handlePlacePick} />
                                                </div>
                                            </div>
                                        )}
                                        <div
                                            style={{
                                                position: 'relative',
                                                width: '100%',
                                                height: 320,
                                                borderRadius: 8,
                                                overflow: 'hidden',
                                                border: '1px solid #eee',
                                            }}
                                        >
                                            <Map
                                                mapId={MAP_ID}
                                                defaultCenter={position || START_POS}
                                                defaultZoom={position ? 16 : 12}
                                                disableDefaultUI={false}
                                                streetViewControl={false}
                                                mapTypeControl={true}
                                                gestureHandling="greedy"
                                            >
                                                {position && <PanTo position={position} />}
                                                {position && <AdvancedMarker position={position} />}
                                                {isEditingLocation && <MapClick onPick={handleMapPick} />}
                                            </Map>

                                            {!isEditingLocation && position && (
                                                <button
                                                    type="button"
                                                    onClick={() => setIsEditingLocation(true)}
                                                    style={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        background: 'rgba(255,255,255,0.35)',
                                                        backdropFilter: 'blur(1px)',
                                                        pointerEvents: 'auto',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: '#374151',
                                                        fontWeight: 600,
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                    }}
                                                    title="Tap the map to unlock and edit"
                                                    aria-label="Unlock location editing"
                                                >
                                                    Location locked — tap the map to edit
                                                </button>
                                            )}
                                        </div>
                                        <div className="creq-map-hint">
                                            {position
                                                ? `📍 ${loc.google_place_desc || `Pinned: ${Number(loc.latitude).toFixed(5)}, ${Number(loc.longitude).toFixed(5)}`}`
                                                : 'Search above or tap the map to pin your building.'}
                                        </div>
                                    </div>
                                </APIProvider>
                            )}

                            {/* ── Rooms & beds ── */}
                            <div className="creq-section-label">Rooms &amp; beds</div>
                            <div className="creq-grid">
                                <label className="creq-label">
                                    Bedrooms {tag('bedrooms')}
                                    <Select
                                        options={BEDROOM_OPTIONS}
                                        value={BEDROOM_OPTIONS.find((o) => o.value === Number(form.bedrooms)) || null}
                                        onChange={(opt) => { const bd = opt ? opt.value : 0; set({ bedrooms: bd, required_cleaners: bd >= 3 ? 2 : 1 }); }}
                                        styles={coralSelectStyles}
                                        theme={coralSelectTheme}
                                        menuPortalTarget={document.body}
                                        isSearchable={false}
                                    />
                                </label>
                                <label className="creq-label">
                                    Bathrooms {tag('bathrooms')}
                                    <Select
                                        options={BATHROOM_OPTIONS}
                                        value={BATHROOM_OPTIONS.find((o) => o.value === Number(form.bathrooms)) || null}
                                        onChange={(opt) => set({ bathrooms: opt ? opt.value : 0 })}
                                        styles={coralSelectStyles}
                                        theme={coralSelectTheme}
                                        menuPortalTarget={document.body}
                                        isSearchable={false}
                                    />
                                </label>
                                </div>
                            <div className="creq-grid creq-grid--3">
                                <label className="creq-label">
                                    Queen / King Size beds {tag('double_beds')}
                                    <Select
                                        options={BED_OPTIONS}
                                        value={BED_OPTIONS.find((o) => o.value === Number(form.double_beds)) || null}
                                        onChange={(opt) => set({ double_beds: opt ? opt.value : 0 })}
                                        styles={coralSelectStyles}
                                        theme={coralSelectTheme}
                                        menuPortalTarget={document.body}
                                        isSearchable={false}
                                    />
                                </label>
                                <label className="creq-label">
                                    Super King Size beds {tag('super_king_beds')}
                                    <Select
                                        options={BED_OPTIONS}
                                        value={BED_OPTIONS.find((o) => o.value === Number(form.super_king_beds)) || null}
                                        onChange={(opt) => set({ super_king_beds: opt ? opt.value : 0 })}
                                        styles={coralSelectStyles}
                                        theme={coralSelectTheme}
                                        menuPortalTarget={document.body}
                                        isSearchable={false}
                                    />
                                </label>
                                <label className="creq-label">
                                    Single beds {tag('single_beds')}
                                    <Select
                                        options={BED_OPTIONS}
                                        value={BED_OPTIONS.find((o) => o.value === Number(form.single_beds)) || null}
                                        onChange={(opt) => set({ single_beds: opt ? opt.value : 0 })}
                                        styles={coralSelectStyles}
                                        theme={coralSelectTheme}
                                        menuPortalTarget={document.body}
                                        isSearchable={false}
                                    />
                                </label>
                            </div>
                            {(changes.bedrooms || changes.single_beds || changes.double_beds || changes.super_king_beds) && (
                                <div className="creq-hintline">
                                    Room and bed changes can affect your All Inclusive price — our team
                                    will confirm any new price before approving.
                                </div>
                            )}
                            {bedsInvalid && (
                                <div className="creq-error">The property must have at least one bed.</div>
                            )}

                            {/* ── Cleaner allocation (2BR extra-cleaner option, as in create) ── */}
                            {Number(form.bedrooms) === 2 && (
                                <div className="creq-cleaners-row">
                                    <div className="creq-cleaners-label">
                                        Cleaner allocation {tag('required_cleaners')} — 2-bedroom apartments can request an extra cleaner for AED 40:
                                    </div>
                                    <div className="creq-segmented">
                                        <button
                                            type="button"
                                            className={`creq-segment ${Number(form.required_cleaners) < 2 ? 'is-active' : ''}`}
                                            onClick={() => set({ required_cleaners: 1 })}
                                        >
                                            <strong>Standard · 1 cleaner</strong>
                                            <span>4 hours</span>
                                        </button>
                                        <button
                                            type="button"
                                            className={`creq-segment ${Number(form.required_cleaners) >= 2 ? 'is-active' : ''}`}
                                            onClick={() => set({ required_cleaners: 2 })}
                                        >
                                            <strong>Extra cleaner · 2 cleaners</strong>
                                            <span>2.5 hours · + AED 40</span>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* ── Extra amenities ── */}
                            <div className="creq-section-label">
                                Extra Amenities &amp; Services
                                {(changes.is_beach_towel || changes.is_dolce_gusto || changes.is_nespresso) && <span className="creq-changed-tag">changed</span>}
                            </div>
                            <div className="creq-linen-note">
                                ✓ Standard Linen Hire and Amenities always included
                            </div>
                            <div className="creq-toggle-grid">
                                <Toggle
                                    checked={form.is_beach_towel}
                                    onChange={(on) => set({ is_beach_towel: on })}
                                    label="Beach towels"
                                    hint="AED 7 per towel"
                                />
                                <Toggle
                                    checked={form.is_dolce_gusto}
                                    onChange={(on) => set({ is_dolce_gusto: on })}
                                    label="Dolce Gusto capsules"
                                    hint="4 capsules · AED 8 per bedroom"
                                />
                                <Toggle
                                    checked={form.is_nespresso}
                                    onChange={(on) => set({ is_nespresso: on })}
                                    label="Nespresso capsules"
                                    hint="4 capsules · AED 5 per bedroom"
                                />
                            </div>

                            {/* ── Access & WiFi ── */}
                            <div className="creq-section-label">Access &amp; WiFi</div>
                            <label className="creq-label creq-label--wide">
                                Access notes {tag('property_access')}
                                <textarea
                                    maxLength={1000}
                                    placeholder="How do our cleaners get in? (lockbox, reception, key…)"
                                    value={form.property_access}
                                    onChange={(e) => set({ property_access: e.target.value })}
                                />
                            </label>
                            <div className="creq-grid">
                                <label className="creq-label">
                                    WiFi name {tag('wifi_name')}
                                    <input
                                        type="text"
                                        maxLength={120}
                                        value={form.wifi_name}
                                        onChange={(e) => set({ wifi_name: e.target.value })}
                                    />
                                </label>
                                <label className="creq-label">
                                    WiFi password {tag('wifi_password')}
                                    <input
                                        type="text"
                                        maxLength={120}
                                        value={form.wifi_password}
                                        onChange={(e) => set({ wifi_password: e.target.value })}
                                    />
                                </label>
                            </div>

                            {/* ── Current setup photos — remove or edit their notes ── */}
                            {existingPhotos.length > 0 && (
                                <>
                                    <div className="creq-section-label">
                                        Current setup photos
                                        {(removedIds.length > 0 || photoDescUpdates.length > 0) && (
                                            <span className="creq-changed-tag">
                                                {[
                                                    removedIds.length ? `${removedIds.length} to remove` : null,
                                                    photoDescUpdates.length ? `${photoDescUpdates.length} note${photoDescUpdates.length > 1 ? 's' : ''} changed` : null,
                                                ].filter(Boolean).join(' · ')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="creq-photos-note">
                                        Already on your property — remove one or change its note and it
                                        takes effect once the request is approved.
                                    </div>
                                    <div className="creq-photos-list">
                                        {existingPhotos.map((ph) => {
                                            const isRemoved = removedIds.includes(ph.id);
                                            const isTask = taskEdits[ph.id] ?? !!ph.task_id;
                                            return (
                                                <div
                                                    className={`creq-photo-item${isRemoved ? ' creq-photo-item--removed' : ''}`}
                                                    key={ph.id}
                                                >
                                                    <img src={ph.file_url} alt="Setup" />
                                                    <div className="creq-photo-fields">
                                                        {isRemoved ? (
                                                            <div className="creq-photo-removednote">
                                                                Will be removed once approved
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <input
                                                                    type="text"
                                                                    maxLength={300}
                                                                    placeholder="Optional note — e.g. “Decoration always on this table”"
                                                                    value={descEdits[ph.id] ?? (ph.description || '')}
                                                                    onChange={(e) => setDescEdits((prev) => ({ ...prev, [ph.id]: e.target.value }))}
                                                                />
                                                                <Toggle
                                                                    className="creq-toggle--compact"
                                                                    checked={!!isTask}
                                                                    onChange={() => toggleTaskExisting(ph)}
                                                                    label="Request cleaner to send photo report at every visit"
                                                                    hint="The cleaner photographs this spot on every visit"
                                                                />
                                                            </>
                                                        )}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="creq-photo-remove"
                                                        onClick={() => toggleRemoveExisting(ph.id)}
                                                        title={isRemoved ? 'Keep this photo' : 'Remove this photo'}
                                                    >
                                                        <FontAwesomeIcon icon={isRemoved ? faRotateLeft : faTrash} />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}

                            {/* ── Add setup photos ── */}
                            <div className="creq-section-label">
                                Add setup photos (optional)
                                {photos.length > 0 && <span className="creq-changed-tag">{photos.length} new</span>}
                            </div>
                            <div className="creq-photos-note">
                                New photos are added to your property once the change is approved —
                                e.g. how areas should look when each guest arrives.
                            </div>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                style={{ display: 'none' }}
                                onChange={handlePickPhotos}
                            />

                            {photos.length > 0 && (
                                <div className="creq-photos-list">
                                    {photos.map((photo, index) => (
                                        <div className="creq-photo-item" key={photo.previewUrl}>
                                            <img src={photo.previewUrl} alt={`Setup ${index + 1}`} />
                                            <div className="creq-photo-fields">
                                                <input
                                                    type="text"
                                                    maxLength={300}
                                                    placeholder="Optional note — e.g. “Decoration always on this table”"
                                                    value={photo.description}
                                                    onChange={(e) => setPhotoDescription(index, e.target.value)}
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                className="creq-photo-remove"
                                                onClick={() => removePhoto(index)}
                                                title="Remove photo"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {photos.length < 10 && (
                                <div
                                    className={`creq-dropzone ${dragOver ? 'is-dragover' : ''}`}
                                    onClick={() => fileInputRef.current?.click()}
                                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                    onDragLeave={() => setDragOver(false)}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        setDragOver(false);
                                        addPhotoFiles(Array.from(e.dataTransfer.files || []));
                                    }}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <FontAwesomeIcon icon={faCamera} className="creq-dropzone-icon" />
                                    <div className="creq-dropzone-text">
                                        <strong>Drop photos here</strong> or click to browse
                                    </div>
                                    <div className="creq-dropzone-hint">Up to 10 images · notes optional</div>
                                </div>
                            )}

                            {/* ── Tasks (after setup pictures, before notes) ── */}
                            <div className="creq-section-label">
                                Tasks for every visit
                                {propTasksChanged && <span className="creq-changed-tag">changed</span>}
                            </div>
                            <div className="creq-photos-note">
                                Instructions the cleaner follows on every visit to this property. Photo tasks created
                                from your setup pictures appear here automatically.
                            </div>
                            <TaskListEditor
                                tasks={propTasks}
                                onChange={setPropTasks}
                                photoTasks={photoTasksForEditor}
                                addLabel="Add a task for this property"
                                emptyHint="No property tasks yet. Add instructions for every cleaning here."
                                disabled={submitting}
                            />

                            {/* ── Notes ── */}
                            <div className="creq-section-label">Notes for every visit {tag('notes')}</div>
                            <label className="creq-label creq-label--wide">
                                <textarea
                                    maxLength={1000}
                                    placeholder="Anything our team should know on every visit…"
                                    value={form.notes}
                                    onChange={(e) => set({ notes: e.target.value })}
                                />
                            </label>

                            {/* ── Recalculated price — at the bottom, like the create form ── */}
                            {pricingTouched && (
                                priceLoading ? (
                                    <div className="creq-price-panel creq-price-loading">
                                        <span className="creq-price-spinner" aria-hidden="true" />
                                        Recalculating your All Inclusive price…
                                    </div>
                                ) : price ? (
                                    <div className="creq-price-panel">
                                        <div className="creq-price-head">
                                            <div>
                                                <div className="creq-price-title">New All Inclusive Price (if approved)</div>
                                                <div className="creq-price-sub">
                                                    Recalculated from your changes · current price:{' '}
                                                    {property.is_custom_price && property.custom_price != null
                                                        ? money(property.custom_price)
                                                        : 'standard rate'}
                                                </div>
                                            </div>
                                            <div className="creq-price-total">{money(price.total)}</div>
                                        </div>
                                        {Array.isArray(price.breakdown) && price.breakdown.length > 1 && (
                                            <div className="creq-price-breakdown">
                                                {price.breakdown.map((row) => (
                                                    <div className="creq-price-row" key={row.label}>
                                                        <span>{row.label}</span>
                                                        <span>{money(row.amount)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <div className="creq-price-breakdown">
                                            <div className="creq-price-row">
                                                <span>VAT (5%)</span>
                                                <span>{money(price.vat)}</span>
                                            </div>
                                            <div className="creq-price-row creq-price-row--strong">
                                                <span>Total incl. VAT</span>
                                                <span>{money(price.total_incl_vat)}</span>
                                            </div>
                                        </div>
                                        <div className="creq-price-footnote">
                                            Your property price is updated to this once our team approves the change.
                                        </div>
                                    </div>
                                ) : null
                            )}

                            {!!error && <div className="creq-error">{error}</div>}
                        </div>

                        <div className="creq-modal-actions">
                            <button className="creq-btn creq-btn--ghost" onClick={onClose} disabled={submitting}>Cancel</button>
                            <button
                                className="creq-btn creq-btn--primary"
                                onClick={submit}
                                disabled={!hasChanges || bedsInvalid || submitting || (pricingTouched && priceLoading)}
                                title={!hasChanges ? 'Change something first'
                                    : (pricingTouched && priceLoading) ? 'Recalculating price…' : undefined}
                            >
                                {submitting ? 'Submitting…'
                                    : (pricingTouched && priceLoading) ? 'Recalculating…' : 'Submit Changes'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
